// @ts-check
// Configurações > Assinatura: situação, planos, contratação pelo Asaas (fatura hospedada com boleto, Pix ou
// cartão), cobranças e cancelamento. Membros veem a situação; só administradores contratam.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag, loadSession, sessionState } from '../../data/metadata-store.js';
import { cancelSubscription, checkoutSubscription, fetchBillingOverview } from '../../data/operations.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { Skeleton } from '../../ui/feedback.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDate } from '../../lib/format.js';
import { formatCents, operationsErrorMessage, showOperationsError, subscriptionStatusColor } from './operations-format.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/operations.js').BillingOverview} BillingOverview */
/** @typedef {import('../../data/operations.js').BillingPlan} BillingPlan */

const BILLING_TYPES = ['UNDEFINED', 'BOLETO', 'PIX', 'CREDIT_CARD'];

/** @param {{ overview: BillingOverview }} props */
function CurrentSubscription({ overview }) {
  const { subscription, state, plans } = overview;
  const plan = plans.find((candidate) => candidate.key === subscription?.planKey);
  const status = state.status;
  /** @type {Array<[string, string]>} */
  const details = [];
  if (plan) details.push([t('operations.billing.plan'), plan.period === 'ANNUAL'
    ? `${plan.name} (${formatCents(plan.priceCents)} ${t('operations.billing.perYear')})`
    : `${plan.name} (${formatCents(plan.priceCents)} ${t('operations.billing.perMonth')})`]);
  if (subscription?.status === 'TRIALING' && subscription.trialEndsAt) details.push([t('operations.billing.trialEndsAt'), formatDate(subscription.trialEndsAt)]);
  if (subscription?.currentPeriodEndsAt) details.push([t('operations.billing.paidUntil'), formatDate(subscription.currentPeriodEndsAt)]);
  if (state.accessMode !== 'FULL' && state.readOnlyAt) details.push([t('operations.billing.readOnlyAt'), formatDate(state.readOnlyAt)]);
  if (subscription?.billingType) details.push([t('operations.billing.billingType'), t(`operations.billing.billingTypes.${subscription.billingType}`)]);
  return html`<div class="channel-card">
    <div class="channel-card-header">
      <${Icon} name="IconReceipt" size=${24} />
      <div class="channel-card-titles">
        <span class="channel-card-title">${plan?.name ?? t(`operations.billing.statuses.${status}`)}</span>
        <span class="channel-card-description">${t(`operations.billing.accessModes.${state.accessMode}`)}</span>
      </div>
      <${Tag} color=${subscriptionStatusColor(status)} text=${t(`operations.billing.statuses.${status}`)} />
    </div>
    ${status === 'LEGACY' ? html`<p class="channel-card-description">${t('operations.billing.legacyDescription')}</p>` : null}
    ${plan?.isLegacy ? html`<p class="channel-card-description">${t('operations.billing.legacyPlanDescription')}</p>` : null}
    ${details.length > 0
      ? html`<dl class="channel-card-details">${details.map(([label, value]) => html`<div key=${label}><dt>${label}</dt><dd>${value}</dd></div>`)}</dl>`
      : null}
  </div>`;
}

/** Nome do cartão de um plano por módulo: só os módulos, sem o período (o período é o seletor acima). @param {BillingPlan} plan */
function planCardName(plan) {
  if (!plan.modules?.length) return plan.name;
  return plan.modules.map((module) => t(`operations.billing.modules.${module}`)).join(' + ');
}

/** @param {{ plan: BillingPlan, isCurrent: boolean, isSelected: boolean, isAdmin: boolean, onSelect: () => void }} props */
function PlanCard({ plan, isCurrent, isSelected, isAdmin, onSelect }) {
  const monthlyCents = plan.monthlyPriceCents ?? plan.priceCents;
  return html`<div class="billing-plan" data-selected=${isSelected ? 'true' : undefined} data-testid=${`billing-plan-${plan.key}`}>
    <div class="billing-plan-header">
      <span class="billing-plan-name">${planCardName(plan)}</span>
      ${isCurrent ? html`<${Tag} color="blue" text=${t('operations.billing.currentPlan')} />` : null}
    </div>
    <div class="billing-plan-price">${formatCents(monthlyCents)}<span>${t('operations.billing.perMonth')}</span></div>
    <p class="billing-plan-description">${plan.period === 'ANNUAL' ? t('operations.billing.annualBilled', { amount: formatCents(plan.priceCents) }) : plan.description}</p>
    <ul class="billing-plan-features">
      ${plan.modules?.length
        ? plan.modules.map((module) => html`<li key=${module}><${Icon} name="IconCheck" size=${14} />${t(`operations.billing.moduleDescriptions.${module}`)}</li>`)
        : html`
          <li><${Icon} name="IconUsers" size=${14} />${plan.maximumMembers ? t('operations.billing.members', { count: plan.maximumMembers }) : t('operations.billing.unlimitedMembers')}</li>
          <li><${Icon} name="IconUser" size=${14} />${plan.maximumPeople ? t('operations.billing.people', { count: plan.maximumPeople.toLocaleString('pt-BR') }) : t('operations.billing.unlimitedPeople')}</li>
          ${plan.features.map((feature) => html`<li key=${feature}><${Icon} name="IconCheck" size=${14} />${t(`operations.billing.features.${feature}`)}</li>`)}`}
    </ul>
    ${isAdmin
      ? html`<${Button} title=${isSelected ? t('operations.billing.selectedPlan') : t('operations.billing.choosePlan')} variant=${isSelected ? 'primary' : 'secondary'}
          accent=${isSelected ? 'blue' : 'default'} size="small" fullWidth=${true} onClick=${onSelect} />`
      : null}
  </div>`;
}

/** @param {{ overview: BillingOverview, planKey: string, onDone: (invoiceUrl: string | null) => void }} props */
function CheckoutForm({ overview, planKey, onDone }) {
  const subscription = overview.subscription;
  const [values, setValues] = useState({
    billingType: subscription?.billingType ?? 'UNDEFINED',
    billingName: subscription?.billingName ?? sessionState.value?.tenant?.displayName ?? '',
    billingEmail: subscription?.billingEmail ?? sessionState.value?.user?.email ?? '',
    billingDocument: subscription?.billingDocument ?? '',
  });
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!values.billingName.trim() || !values.billingEmail.trim() || !values.billingDocument.trim()) {
      setError(t('operations.billing.requiredFields'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const result = await checkoutSubscription({ planKey, ...values });
      showSuccessSnackbar(t('operations.billing.subscribed'));
      onDone(result.invoiceUrl);
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  /** @param {'billingName' | 'billingEmail' | 'billingDocument'} name @param {string} [type] */
  const field = (name, type = 'text') => html`<label class="form-field">
    <span class="form-field-label">${t(`operations.billing.${name}`)}</span>
    <input class="text-input" type=${type} name=${name} value=${values[name]} autocomplete="off"
      onInput=${(event) => setValues({ ...values, [name]: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
  </label>`;

  return html`<form class="channel-card channel-form" onSubmit=${submit}>
    <div class="billing-type-options" role="radiogroup" aria-label=${t('operations.billing.billingType')}>
      ${BILLING_TYPES.map((billingType) => html`<label key=${billingType} class="billing-type-option" data-selected=${values.billingType === billingType ? 'true' : undefined}>
        <input type="radio" name="billingType" value=${billingType} checked=${values.billingType === billingType}
          onChange=${() => setValues({ ...values, billingType })} />
        <span>${t(`operations.billing.billingTypes.${billingType}`)}</span>
      </label>`)}
    </div>
    <div class="form-field-row">${field('billingName')}${field('billingDocument')}</div>
    ${field('billingEmail', 'email')}
    <span class="input-hint">${t('operations.billing.checkoutDescription')}</span>
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    <div class="channel-form-actions">
      <${Button} title=${subscription?.hasProviderSubscription ? t('operations.billing.changePlan') : t('operations.billing.subscribe')}
        icon="IconCreditCard" accent="blue" size="small" type="submit" isLoading=${isSaving} />
    </div>
  </form>`;
}

/** @param {{ overview: BillingOverview }} props */
function PaymentsTable({ overview }) {
  if (overview.payments.length === 0) return html`<p class="channel-card-description">${t('operations.billing.paymentsEmpty')}</p>`;
  return html`<div class="settings-table">
    <div class="settings-table-row settings-table-header billing-payment-row">
      <span>${t('operations.billing.paymentColumns.dueDate')}</span>
      <span>${t('operations.billing.paymentColumns.value')}</span>
      <span>${t('operations.billing.paymentColumns.status')}</span>
      <span>${t('operations.billing.paymentColumns.paidAt')}</span>
      <span></span>
    </div>
    ${overview.payments.map((payment) => html`<div key=${payment.asaasPaymentId} class="settings-table-row billing-payment-row">
      <span>${formatDate(payment.dueDate)}</span>
      <span>${formatCents(payment.valueCents)}</span>
      <span class="settings-table-muted">${t(`operations.billing.paymentStatuses.${payment.status}`)}</span>
      <span class="settings-table-muted">${formatDate(payment.paidAt)}</span>
      <span>${payment.invoiceUrl && (payment.status === 'PENDING' || payment.status === 'OVERDUE')
        ? html`<a class="operations-link" href=${payment.invoiceUrl} target="_blank" rel="noopener noreferrer">${t('operations.billing.openInvoice')}</a>`
        : null}</span>
    </div>`)}
  </div>`;
}

/**
 * Notas fiscais emitidas para a escola (3.3.0): lidas do Asaas, com PDF e XML. Só quem tem a flag BILLING vê.
 * @param {{ overview: BillingOverview }} props
 */
function InvoicesTable({ overview }) {
  if (!overview.invoices || overview.invoices.length === 0) return html`<p class="channel-card-description">${t('operations.billing.invoicesEmpty')}</p>`;
  return html`<div class="settings-table">
    <div class="settings-table-row settings-table-header billing-invoice-row">
      <span>${t('operations.billing.invoiceColumns.number')}</span>
      <span>${t('operations.billing.invoiceColumns.effectiveDate')}</span>
      <span>${t('operations.billing.invoiceColumns.value')}</span>
      <span>${t('operations.billing.invoiceColumns.files')}</span>
    </div>
    ${overview.invoices.map((invoice) => html`<div key=${invoice.id} class="settings-table-row billing-invoice-row" title=${invoice.serviceDescription ?? ''}>
      <span>${invoice.number ? t('operations.billing.invoiceNumbered', { number: invoice.number }) : t('operations.billing.invoiceNoNumber')}</span>
      <span class="settings-table-muted">${invoice.effectiveDate ? formatDate(`${invoice.effectiveDate}T12:00:00.000Z`) : '-'}</span>
      <span>${formatCents(Math.round(invoice.value * 100))}</span>
      <span class="billing-invoice-files">
        ${invoice.pdfUrl ? html`<a class="operations-link" href=${invoice.pdfUrl} target="_blank" rel="noopener noreferrer">${t('operations.billing.downloadPdf')}</a>` : null}
        ${invoice.xmlUrl ? html`<a class="operations-link" href=${invoice.xmlUrl} target="_blank" rel="noopener noreferrer">${t('operations.billing.downloadXml')}</a>` : null}
      </span>
    </div>`)}
  </div>`;
}

export function SettingsBillingPage() {
  const [overview, setOverview] = useState(/** @type {BillingOverview | null} */ (null));
  const [selectedPlanKey, setSelectedPlanKey] = useState(/** @type {string | null} */ (null));
  const [period, setPeriod] = useState(/** @type {'MONTHLY' | 'ANNUAL'} */ ('MONTHLY'));
  const [invoiceUrl, setInvoiceUrl] = useState(/** @type {string | null} */ (null));
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  // assinatura e cobrança seguem a flag BILLING do papel
  const isAdmin = hasCurrentPermissionFlag('BILLING');

  const load = () => fetchBillingOverview().then(setOverview).catch(showErrorSnackbar);
  useEffect(() => {
    document.title = `${t('operations.billing.title')} - ${t('navigation.settings')}`;
    load();
  }, []);

  const refreshAll = async () => {
    await load();
    // a faixa de aviso vem da sessão
    await loadSession().catch(() => null);
  };

  const cancel = async () => {
    setIsCanceling(true);
    try {
      await cancelSubscription();
      showSuccessSnackbar(t('operations.billing.canceled'));
      setIsCancelOpen(false);
      await refreshAll();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsCanceling(false);
    }
  };

  const header = html`<${PageHeader} icon="IconReceipt" title=${t('operations.billing.title')} />`;
  if (!overview) {
    return html`<${PageCard} header=${header}><div class="settings-page"><${Skeleton} height=${140} radius=${8} /><${Skeleton} height=${260} radius=${8} /></div><//>`;
  }
  const currentPlanKey = overview.subscription?.planKey ?? null;
  const canCancel = isAdmin && overview.subscription && overview.subscription.status !== 'CANCELED' && overview.subscription.hasProviderSubscription;
  // planos por módulo em mensal e anual: o seletor escolhe o período e os cartões mostram só os módulos; planos sem
  // período (BILLING_PLANS_JSON) aparecem nos dois; o plano antigo da escola nunca entra na escolha
  const contractablePlans = overview.plans.filter((plan) => !plan.isLegacy);
  const hasPeriods = contractablePlans.some((plan) => plan.period === 'ANNUAL');
  const visiblePlans = contractablePlans.filter((plan) => !hasPeriods || !plan.period || plan.period === period);
  return html`<${PageCard} header=${header}>
    <div class="settings-page">
      <${SettingsSection} title=${t('operations.billing.currentTitle')} description=${t('operations.billing.currentDescription')}>
        <${CurrentSubscription} overview=${overview} />
        ${invoiceUrl
          ? html`<div class="operations-notice" role="status">
              <${Icon} name="IconCircleCheck" size=${16} />
              <span>${t('operations.billing.invoiceReady')}</span>
              <a class="operations-link" href=${invoiceUrl} target="_blank" rel="noopener noreferrer">${t('operations.billing.openInvoice')}</a>
            </div>`
          : null}
      <//>
      <${SettingsSection} title=${t('operations.billing.plansTitle')} description=${t('operations.billing.plansDescription')}>
        ${hasPeriods ? html`<div class="billing-period-toggle"><${SegmentedControl} label=${t('operations.billing.period')} value=${period} name="billing-period"
          options=${[{ value: 'MONTHLY', label: t('operations.billing.periods.MONTHLY') }, { value: 'ANNUAL', label: t('operations.billing.periods.ANNUAL'), hint: t('operations.billing.annualHint') }]}
          onChange=${(/** @type {'MONTHLY' | 'ANNUAL'} */ value) => {
            setPeriod(value);
            setSelectedPlanKey(null);
          }} /></div>` : null}
        <div class="billing-plans">
          ${visiblePlans.map((plan) => html`<${PlanCard} key=${plan.key} plan=${plan} isAdmin=${isAdmin}
            isCurrent=${plan.key === currentPlanKey && overview.subscription?.status !== 'CANCELED'}
            isSelected=${plan.key === selectedPlanKey} onSelect=${() => setSelectedPlanKey(plan.key)} />`)}
        </div>
        ${!isAdmin ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('operations.billing.adminOnly')}</p>` : null}
      <//>
      ${isAdmin && selectedPlanKey
        ? html`<${SettingsSection} title=${t('operations.billing.checkoutTitle')}>
            ${overview.isProviderConfigured
              ? html`<${CheckoutForm} overview=${overview} planKey=${selectedPlanKey} onDone=${async (/** @type {string | null} */ url) => {
                  setInvoiceUrl(url);
                  setSelectedPlanKey(null);
                  await refreshAll();
                }} />`
              : html`<p class="channel-card-note"><${Icon} name="IconInfoCircle" size=${14} />${t('operations.billing.providerNotConfigured')}</p>`}
          <//>`
        : null}
      ${isAdmin
        ? html`<${SettingsSection} title=${t('operations.billing.paymentsTitle')}><${PaymentsTable} overview=${overview} /><//>`
        : null}
      ${isAdmin && overview.isProviderConfigured
        ? html`<${SettingsSection} title=${t('operations.billing.invoicesTitle')} description=${t('operations.billing.invoicesDescription')}><${InvoicesTable} overview=${overview} /><//>`
        : null}
      ${canCancel
        ? html`<${SettingsSection} title=${t('operations.billing.cancelTitle')} description=${t('operations.billing.cancelDescription')}>
            <div><${Button} title=${t('operations.billing.cancel')} icon="IconCancel" variant="secondary" accent="danger" size="small" onClick=${() => setIsCancelOpen(true)} /></div>
          <//>`
        : null}
    </div>
    ${isCancelOpen
      ? html`<${Modal} title=${t('operations.billing.cancelTitle')} subtitle=${t('operations.billing.cancelDescription')} confirmLabel=${t('operations.billing.cancel')}
          confirmAccent="danger" isLoading=${isCanceling} onClose=${() => setIsCancelOpen(false)} onConfirm=${cancel} />`
      : null}
  <//>`;
}
